import { Layout } from 'components/Layout/Layout';
import { UserContext } from 'context/userContext';
import { useContext, useState } from 'react';
import styles from '../styles/pages/ThemeSettings.module.css';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import { PLAYER_SELECTABLE_THEMES } from 'lib/theme.config';
import { normalizePlayerTheme, DEFAULT_PLAYER_THEME } from 'lib/playerTheme';
import { ToastMessagesIDs } from 'types/enums';
import {
  PLAYER_LAYOUT_IDS,
  setPlayerLayout,
  setVipMode,
  usePlayerLayout,
  useVipMode,
  VipMode,
} from 'lib/playerLayout';

export const ThemeSettings = () => {
  const { loggedUser, updateTheme } = useContext(UserContext);
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const [saving, setSaving] = useState<string | null>(null);
  const layout = usePlayerLayout();
  const vipMode = useVipMode();

  if (!loggedUser) {
    navigate('/login');
    return null;
  }

  const currentTheme = normalizePlayerTheme(
    loggedUser.player_theme ?? DEFAULT_PLAYER_THEME
  );

  const handleSelect = async (themeId: string) => {
    if (themeId === currentTheme || saving) return;
    setSaving(themeId);
    const ok = await updateTheme(themeId);
    setSaving(null);
    if (!ok) {
      toast(t('theme_update_error_message'), {
        toastId: ToastMessagesIDs.CHANGE_PASSWORD_ERROR,
      });
    }
  };

  // The legacy tottalo palette has no modern look (see lib/playerLayout.ts).
  const layoutGrid = currentTheme !== 'tottalo' && (
    <>
      <p className={styles.sectionLabel}>{t('layout_settings_label')}</p>
      <div className={styles.grid}>
        {PLAYER_LAYOUT_IDS.filter((x) => x !== 'vip' || isMobile).map(
          (value) => {
            const isActive = value === layout;
            return (
              <button
                key={value}
                type="button"
                data-player-theme={currentTheme}
                data-layout={value}
                className={classNames(
                  styles.card,
                  isActive && styles.cardActive
                )}
                onClick={() => setPlayerLayout(value)}
              >
                <span
                  className={classNames(styles.preview, styles.layoutPreview)}
                >
                  <span className={styles.previewChrome} />
                  <span className={styles.previewRow} />
                  <span className={styles.previewRow} />
                  <span className={styles.previewAccent} />
                </span>
                <span className={styles.name}>{t(`layout_${value}`)}</span>
                {isActive && (
                  <ThemeIcon
                    icon="success-checkmark"
                    alt=""
                    variant="inline"
                    className={styles.activeMark}
                  />
                )}
              </button>
            );
          }
        )}
      </div>
      <p className={styles.sectionLabel}>{t('palette_settings_label')}</p>
    </>
  );

  const grid = (
    <div className={styles.grid}>
      {PLAYER_SELECTABLE_THEMES.map(({ value, name }) => {
        const isActive = value === currentTheme;
        return (
          <button
            key={value}
            type="button"
            data-player-theme={value}
            className={classNames(styles.card, isActive && styles.cardActive)}
            onClick={() => handleSelect(value)}
            disabled={saving !== null}
          >
            <span className={styles.preview}>
              <span className={styles.previewChrome} />
              <span className={styles.previewContent} />
              <span className={styles.previewAccent} />
            </span>
            <span className={styles.name}>{name}</span>
            {isActive && (
              <ThemeIcon
                icon="success-checkmark"
                alt=""
                variant="inline"
                className={styles.activeMark}
              />
            )}
          </button>
        );
      })}
    </div>
  );

  // VIP's background, under the colours; it holds for all of them.
  const modeSwitch = layout === 'vip' && (
    <>
      <p className={styles.sectionLabel}>{t('mode_settings_label')}</p>
      <div className={styles.modeSwitch} role="group">
        {(['dark', 'light'] as VipMode[]).map((mode) => (
          <button
            key={mode}
            type="button"
            className={classNames(mode === vipMode && styles.modeOn)}
            aria-pressed={mode === vipMode}
            onClick={() => setVipMode(mode)}
          >
            {t(`mode_${mode}`)}
          </button>
        ))}
      </div>
    </>
  );

  return (
    <>
      {!isMobile ? (
        <Layout>
          <div className={styles.wrapper}>
            <div className={styles.container}>
              <p className={styles.title}>{t('theme_settings_label')}</p>
              <p className={styles.subtitle}>{t('theme_settings_hint')}</p>
              {layoutGrid}
              {grid}
            </div>
          </div>
        </Layout>
      ) : (
        <MobileLayout title={t('theme_settings_label')}>
          <p className={styles.subtitleMobile}>{t('theme_settings_hint')}</p>
          {layoutGrid}
          {grid}
          {modeSwitch}
        </MobileLayout>
      )}
    </>
  );
};
